import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Typography } from 'antd';
import React, { useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import DownloadIcon from 'src/assets/icons/download.png';
import ReloadIcon from 'src/assets/icons/reload.png';
import Icon from 'src/components/v2/Icon';
import useDownload from 'src/hooks/useDownload';
import { useChatStore } from 'src/stores/chatStore';
import { IFilePath } from 'src/types/helpdesk/topic.type';
import { formatDate, getFilePreview, isImage } from 'src/utils/utils';
import MessageImage from './MessageImage';
import './MessageImage.scss';

const { Text } = Typography;
type AttachmentType = {
  file: IFilePath;
  isReplying?: boolean;
  className?: string;
  style?: React.CSSProperties;
  isThumbnail?: boolean;
  isActive?: boolean;
  size?: number;
  topicId: string;
  onClick?: (file: IFilePath) => void;
};
const MessageImageContainer = ({
  file,
  isReplying,
  className,
  style,
  isThumbnail,
  isActive,
  topicId,
  size,
  onClick
}: AttachmentType) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  const queryClient = useQueryClient();
  const download = useDownload();

  // ✅ Alternative: Direct access to specific file
  const fileKey = useMemo(() => file?.path || `file-${file?.id}`, [file]);
  const cachedFile = useChatStore(useCallback((state) => state.files[fileKey], [fileKey]));

  // ✅ Stable thumbnail media URL
  const thumbnailMedia = useMemo(() => {
    if (!cachedFile) return file?.path;

    return cachedFile.url || file?.path || null;
  }, [cachedFile, file?.path]);

  const handleReload = () => {
    queryClient.invalidateQueries({
      queryKey: ['topicMessageImage', file?.id, inView]
    });
  };

  const handleDownload = (image: IFilePath) => {
    download.download(image as IFilePath);
  };

  const handlePreview = (image: IFilePath) => {
    download.preview(image as IFilePath);
  };

  const isImageFile = useMemo(() => isImage(file?.title), [file?.title]);

  // const renderMessageImage = () => {
  //   return (
  //     <>
  //       <MessageImage
  //         file={file}
  //         topicId={topicId}
  //         isReplying={isReplying}
  //         style={style}
  //         inView={inView}
  //         isThumbnail={isThumbnail}
  //         isActive={isActive}
  //         size={size}
  //       />
  //       {!thumbnailMedia && !isReplying && (
  //         <div className='reload' onClick={handleReload}>
  //           <Icon size={40} url={ReloadIcon} />
  //           <Text>Tải lại</Text>
  //         </div>
  //       )}
  //     </>
  //   );
  // };
  // // const renderMessageFile = () => {
  //   return (
  //     <Flex className='file-wrapper' style={style} onClick={() => !isReplying && handlePreview(file)}>
  //       {/* {isVideoFile && (
  //         <video width='320' height='240' controls>
  //           <source src={file?.path} type='video/mp4' />
  //           Trình duyệt của bạn không hỗ trợ video.
  //         </video>
  //       )} */}
  //       <Icon size={size || 50} url={getFilePreview(file?.title)} className='file-thumb' />
  //       <Flex vertical gap={5} justify='space-evenly' className='file-body'>
  //         <p className='filename' style={{ fontWeight: 600 }}>
  //           {file.title || 'Không có tiêu đề'}
  //         </p>
  //         {!isReplying && (
  //           <Flex gap={10} justify='space-between' align='center'>
  //             <Text style={{ fontSize: '11px' }} type='secondary'>
  //               {formatDate(file?.createdDate)}
  //             </Text>
  //             <Button
  //               type='text'
  //               icon={<Icon size={18} url={DownloadIcon} />}
  //               onClick={(e) => {
  //                 e.stopPropagation();
  //                 handleDownload(file);
  //               }}
  //               loading={download.isLoading}
  //             />
  //           </Flex>
  //         )}
  //       </Flex>
  //     </Flex>
  //   );
  // };

  return (
    <div
      ref={ref}
      key={file?.path}
      className={className}
      onClick={() => {
        onClick && onClick(file);
      }}
    >
      {/* {isImageFile ? renderMessageImage() : renderMessageFile()} */}
      {isImageFile ? (
        <>
          <MessageImage
            file={file}
            topicId={topicId}
            isReplying={isReplying}
            style={style}
            inView={inView}
            isThumbnail={isThumbnail}
            isActive={isActive}
            size={size}
          />
          {!thumbnailMedia && !isReplying && (
            <div className='reload' onClick={handleReload}>
              <Icon size={40} url={ReloadIcon} />
              <Text>Tải lại</Text>
            </div>
          )}
        </>
      ) : (
        <Flex className='file-wrapper' style={style} onClick={() => !isReplying && handlePreview(file)}>
          <Icon size={size || 50} url={getFilePreview(file?.title)} className='file-thumb' />
          <Flex vertical gap={5} justify='space-evenly' className='file-body'>
            <p className='filename' style={{ fontWeight: 600 }}>
              {file.title || 'Không có tiêu đề'}
            </p>
            {!isReplying && (
              <Flex gap={10} justify='space-between' align='center'>
                <Text style={{ fontSize: '11px' }} type='secondary'>
                  {formatDate(file?.createdDate)}
                </Text>
                <Button
                  type='text'
                  icon={<Icon size={18} url={DownloadIcon} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDownload(file);
                  }}
                  loading={download.isLoading}
                />
              </Flex>
            )}
          </Flex>
        </Flex>
      )}
    </div>
  );
};

export default React.memo(MessageImageContainer, (prevProps, nextProps) => {
  return (
    prevProps.file?.id === nextProps.file?.id &&
    prevProps.file?.path === nextProps.file?.path &&
    prevProps.isReplying === nextProps.isReplying &&
    prevProps.isActive === nextProps.isActive &&
    prevProps.topicId === nextProps.topicId
  );
});
